/**
 * "What just connected?" and the badge that leads to it.
 *
 * The aircraft has already said what board and cameras it carries
 * (AUTOPILOT_VERSION, CAMERA_INFORMATION), so the form opens with those rows
 * filled and the operator supplies what no protocol carries: the model, the
 * tail number. It opens by itself only for the person who connected the
 * aircraft. Everyone else on the team sees the UNIDENTIFIED badge and opens it
 * when they choose, because the same modal landing on every screen in the unit
 * at once was the predecessor's first version of this.
 *
 * Always skippable. An aircraft connecting under time pressure has to be
 * flyable before it is documented. "Skip for now" is remembered on the server
 * per person and per board, so a refresh does not ask again.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  claimSlot,
  getClaimable,
  identifySlot,
  INVENTORY_KINDS,
  skipIdentify,
  type ComponentInput,
  type IdentifyInput,
  type InventoryItem,
  type InventoryKind,
  type PendingIdentification,
} from '@/api/inventory';
import { useAuthStatus, useHasRole } from '@/stores/authStore';

import { Field, MUTED, OK, PillButton, WARN, WHITE } from './dataUi';
import { nextToAsk, usePendingFor, useIdentifyStore } from './identifyStore';

/** How often the unidentified list is refreshed. A new aircraft heartbeats
 * within a second and reports its board within two, so this is the delay
 * before the operator is asked, not before the server knows. */
const POLL_MS = 8_000;

export function emptyInput(kind: InventoryKind = 'copter'): IdentifyInput {
  return {
    tailNumber: '',
    kind,
    platform: '',
    manufacturer: '',
    serialNumber: '',
    notes: '',
    components: [],
  };
}

function inputFrom(pending: PendingIdentification): IdentifyInput {
  return {
    ...emptyInput(pending.kind),
    components: pending.components.map((c) => ({
      category: c.category,
      make: c.make,
      model: c.model,
      serial: c.serial,
      firmware: c.firmware,
    })),
  };
}

/** The fields of an airframe as an operator describes it. Used here and for
 * cataloguing an aircraft before it has ever connected. */
export function AirframeForm({
  value,
  onChange,
}: {
  value: IdentifyInput;
  onChange: (next: IdentifyInput) => void;
}) {
  const set = <K extends keyof IdentifyInput>(key: K, v: IdentifyInput[K]) =>
    onChange({ ...value, [key]: v });
  const setComponent = (index: number, patch: Partial<ComponentInput>) =>
    set(
      'components',
      value.components.map((c, i) => (i === index ? { ...c, ...patch } : c)),
    );

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-2">
        <Field
          label="TAIL NUMBER"
          value={value.tailNumber}
          onChange={(v) => set('tailNumber', v)}
          placeholder="KESTREL-04"
          mono
        />
        <label className="flex flex-col gap-1">
          <span className="label-mono text-[8px]" style={{ color: MUTED }}>
            KIND
          </span>
          <select
            value={value.kind}
            onChange={(event) => set('kind', event.target.value as InventoryKind)}
            className="well h-8 rounded-[9px] px-2 text-[12px] outline-none"
          >
            {INVENTORY_KINDS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </label>
        <Field
          label="MODEL"
          value={value.platform}
          onChange={(v) => set('platform', v)}
          placeholder="Dingo"
        />
        <Field
          label="MANUFACTURER"
          value={value.manufacturer}
          onChange={(v) => set('manufacturer', v)}
          placeholder="Who built it"
        />
        <Field
          label="AIRFRAME SERIAL"
          value={value.serialNumber}
          onChange={(v) => set('serialNumber', v)}
          placeholder="Off the plate on the fuselage"
          mono
        />
        <Field
          label="NOTES"
          value={value.notes}
          onChange={(v) => set('notes', v)}
          placeholder="Anything the next crew should know"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center">
          <span className="label-mono text-[8px]" style={{ color: MUTED }}>
            HARDWARE
          </span>
          <button
            type="button"
            onClick={() =>
              set('components', [
                ...value.components,
                { category: '', make: '', model: '', serial: '', firmware: '' },
              ])
            }
            className="label-mono ml-auto text-[8.5px]"
            style={{ color: WHITE }}
          >
            ADD PART
          </button>
        </div>
        {value.components.length === 0 ? (
          <p className="text-[10.5px]" style={{ color: MUTED }}>
            Nothing reported yet. Add the GPS, radio, camera or anything else worth tracking.
          </p>
        ) : (
          value.components.map((c, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] items-end gap-1.5 phone:grid-cols-2">
              <Field label="PART" value={c.category} onChange={(v) => setComponent(i, { category: v })} placeholder="gps" />
              <Field label="MAKE" value={c.make} onChange={(v) => setComponent(i, { make: v })} />
              <Field label="MODEL" value={c.model} onChange={(v) => setComponent(i, { model: v })} />
              <Field label="SERIAL" value={c.serial} onChange={(v) => setComponent(i, { serial: v })} mono />
              <button
                type="button"
                onClick={() => set('components', value.components.filter((_, j) => j !== i))}
                className="label-mono mb-2 text-[8.5px]"
                style={{ color: MUTED }}
                aria-label={`Remove part ${i + 1}`}
              >
                DROP
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function BoardSummary({ pending }: { pending: PendingIdentification }) {
  const board = pending.board;
  if (!board) {
    return (
      <p className="text-[11px]" style={{ color: MUTED }}>
        The autopilot has not said which board it is. It may not support AUTOPILOT_VERSION, in
        which case it will be asked about again on every connection.
      </p>
    );
  }
  return (
    <div className="flex flex-wrap gap-x-5 gap-y-1 text-[11px]">
      <span style={{ color: MUTED }}>
        BOARD <span className="font-mono" style={{ color: WHITE }}>{board.uid || 'no serial'}</span>
      </span>
      {board.vendor ? (
        <span style={{ color: MUTED }}>
          VENDOR <span style={{ color: WHITE }}>{board.vendor}</span>
        </span>
      ) : null}
      {board.flightSw ? (
        <span style={{ color: MUTED }}>
          FIRMWARE <span className="font-mono" style={{ color: WHITE }}>{board.flightSw}</span>
        </span>
      ) : null}
    </div>
  );
}

export function IdentifyDialog({
  pending,
  onClose,
  onSkip,
  onDone,
}: {
  pending: PendingIdentification;
  onClose: () => void;
  onSkip: () => void;
  onDone: () => void;
}) {
  const canEdit = useHasRole('operator');
  const [mode, setMode] = useState<'describe' | 'claim'>('describe');
  const [input, setInput] = useState<IdentifyInput>(() => inputFrom(pending));
  const [catalogue, setCatalogue] = useState<InventoryItem[] | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void getClaimable().then((result) => setCatalogue(result.ok ? result.data.airframes : []));
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const save = async () => {
    if (!input.tailNumber.trim()) {
      setMessage('Give it a tail number, even a temporary one.');
      return;
    }
    setBusy(true);
    const result = await identifySlot(pending.droneId, input);
    setBusy(false);
    if (result.ok) onDone();
    else setMessage(result.error.message);
  };

  const claim = async (itemId: string) => {
    setBusy(true);
    const result = await claimSlot(pending.droneId, itemId);
    setBusy(false);
    if (result.ok) onDone();
    else setMessage(result.error.message);
  };

  return createPortal(
    <div
      className="pointer-events-auto fixed inset-0 z-[60] flex items-center justify-center p-6 phone:p-3"
      style={{ background: 'rgba(6,6,8,0.55)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Identify ${pending.droneId}`}
        className="glass scroll-thin flex max-h-full w-[min(620px,100%)] flex-col gap-4 overflow-y-auto rounded-[22px] p-6 phone:p-4"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center gap-2.5">
          <span className="label-mono text-[12px]">WHAT JUST CONNECTED</span>
          <span className="font-mono text-[11px]" style={{ color: WARN }}>
            {pending.droneId}
          </span>
          {pending.simulated ? (
            <span className="label-mono text-[8px]" style={{ color: WARN }}>
              SIM
            </span>
          ) : null}
          <button
            type="button"
            onClick={onClose}
            className="ml-auto font-mono text-[10px] text-white/45 hover:text-white"
          >
            ESC
          </button>
        </div>

        <BoardSummary pending={pending} />

        {!canEdit ? (
          <p className="text-[12px]" style={{ color: MUTED }}>
            Identifying an aircraft needs operator access.
          </p>
        ) : (
          <>
            <div className="flex gap-1.5">
              <PillButton active={mode === 'describe'} onClick={() => setMode('describe')}>
                DESCRIBE IT
              </PillButton>
              <PillButton
                active={mode === 'claim'}
                onClick={() => setMode('claim')}
                disabled={catalogue !== null && catalogue.length === 0}
              >
                ONE WE CATALOGUED{catalogue && catalogue.length ? ` (${catalogue.length})` : ''}
              </PillButton>
            </div>

            {mode === 'describe' ? (
              <AirframeForm value={input} onChange={setInput} />
            ) : (
              <div className="flex flex-col gap-1">
                {(catalogue ?? []).map((a) => (
                  <button
                    key={a.itemId}
                    type="button"
                    disabled={busy}
                    onClick={() => void claim(a.itemId)}
                    className="well flex items-center gap-2 rounded-[10px] px-3 py-2 text-left text-[12px]"
                  >
                    <span className="tnum" style={{ color: WHITE }}>
                      {a.name}
                    </span>
                    <span style={{ color: MUTED }}>{a.platform ?? a.kind}</span>
                    <span className="label-mono ml-auto text-[8.5px]" style={{ color: OK }}>
                      THIS ONE
                    </span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {message ? (
          <p className="text-[11px]" role="status" style={{ color: WARN }}>
            {message}
          </p>
        ) : null}

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onSkip}
            className="label-mono text-[9px]"
            style={{ color: MUTED }}
          >
            SKIP FOR NOW
          </button>
          {canEdit && mode === 'describe' ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => void save()}
              className="ml-auto rounded-[99px] px-4 py-[7px] label-mono text-[9.5px] font-semibold disabled:opacity-50"
              style={{ background: 'oklch(1 0 0 / 0.92)', color: '#0c0c0d' }}
            >
              SAVE TO THE REGISTER
            </button>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Mounted once for the whole app. Keeps the unidentified list fresh, opens the
 * dialog unasked for the operator who connected an aircraft, and renders the
 * dialog whoever opened it.
 */
export function IdentifyPrompt() {
  const status = useAuthStatus();
  const pending = useIdentifyStore((state) => state.pending);
  const openFor = useIdentifyStore((state) => state.openFor);
  const openedBy = useIdentifyStore((state) => state.openedBy);
  const { refresh, open, close, changed } = useIdentifyStore.getState();
  // This session's dismissals, so a prompt closed a moment ago does not
  // reopen on the next poll before the server has recorded the skip.
  const dismissed = useRef(new Set<string>());

  useEffect(() => {
    if (status !== 'authenticated') return;
    void refresh();
    const timer = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [status, refresh]);

  const next = nextToAsk(pending, dismissed.current)?.droneId;
  useEffect(() => {
    if (openFor === null && next) open(next, 'prompt');
  }, [next, openFor, open]);

  const current = pending.find((p) => p.droneId === openFor);

  const skip = useCallback(() => {
    if (!openFor) return;
    dismissed.current.add(openFor);
    void skipIdentify(openFor).then(() => void refresh());
    close();
  }, [openFor, close, refresh]);

  const dismiss = useCallback(() => {
    // Closing a prompt that opened by itself is a "not now" like the button:
    // without it, the next poll would put it straight back.
    if (openedBy === 'prompt') skip();
    else close();
  }, [openedBy, skip, close]);

  const done = useCallback(() => {
    if (openFor) dismissed.current.add(openFor);
    close();
    changed();
    void refresh();
  }, [openFor, close, changed, refresh]);

  if (status !== 'authenticated' || !current) return null;
  return (
    <IdentifyDialog
      key={current.droneId}
      pending={current}
      onClose={dismiss}
      onSkip={skip}
      onDone={done}
    />
  );
}

/** The fleet rail's way into the dialog, for anyone who can see the aircraft. */
export function UnidentifiedBadge({ droneId }: { droneId: string }) {
  const pending = usePendingFor(droneId);
  const open = useIdentifyStore((state) => state.open);
  if (!pending) return null;
  return (
    <button
      type="button"
      onClick={() => open(droneId)}
      title="Nobody has said what this aircraft is yet. Click to identify it."
      className="self-start rounded-[99px] px-1.5 py-px label-mono text-[8px]"
      style={{ color: WARN, background: `color-mix(in oklch, ${WARN} 14%, transparent)` }}
    >
      UNIDENTIFIED
    </button>
  );
}
